/**
 * ノード DTO の境界アダプタ。
 *
 * `/api/v1/ops/nodes` は DB の列名に近い語彙（`allocatedMailboxes` /
 * `storageBytesUsed` / `lastHeartbeatAt` / `updateRing` …）で返す。
 * 画面側は「台帳の列名」ではなく「運用者が読む言葉」で書かれているので、
 * **変換をここ 1 か所に閉じる**（`apps/portal/src/lib/dns-records.ts` と同じ方針）。
 *
 * 原則:
 *  - API を正とし、SPA 側で名前を寄せる。API の形が変わったらこのファイルだけ直す。
 *  - 値が無いときは `undefined` を撒かず、**ゼロ値**（0 / [] / null）にする。
 *    画面が `?.` で握り潰さずに済むよう、形は常に同じにする。
 */
import type {
  NodeApplyState,
  NodeDisplayStatus,
  NodeHealth,
  NodeRole,
  NodeStatus,
  RolloutRing,
} from '@mail-sv/shared';
import type { NodeDetail, NodeServiceState, NodeSummary } from '../types/api.js';

/* --------------------------------------------------------- API の生の形 */

/** `GET /nodes` の 1 件（`toNodeView()` + 実行時メトリクス）。 */
export interface ApiNode {
  id: string;
  nodePoolId: string;
  hostname: string;
  displayName: string;
  region: string;
  role: NodeRole;
  status: NodeStatus;
  health: NodeHealth;
  displayStatus: NodeDisplayStatus;
  publicIpv4: string[];
  publicIpv6: string[];
  acceptsNewAllocations: boolean;
  maintenanceMode: boolean;
  outboundEnabled: boolean;
  allocatedDomains: number;
  allocatedMailboxes: number;
  maxDomains: number;
  maxMailboxes: number;
  storageBytesUsed: number;
  storageBytesTotal: number;
  agentVersion: string | null;
  stackVersion: string | null;
  updateRing: RolloutRing | null;
  desiredGeneration: number;
  appliedGeneration: number;
  lastHeartbeatAt: string | null;
  createdAt: string;
  /**
   * ノード指紋の隔離状態（R74）。非 null の間は enroll も desired-state の
   * 払い出しも止まっている。**これを画面に出さないと「なぜこのノードだけ
   * 適用が進まないのか」が DB を見るまで分からない。**
   */
  fingerprintMismatchAt: string | null;
  fingerprintApprovedAt: string | null;
  /** 承認済みの指紋。先頭 12 文字だけ返る。 */
  nodeFingerprintPrefix: string | null;
  /* 実行時メトリクス（API が常に返す。未報告でもゼロ値） */
  queueDepth: number;
  applyState: NodeApplyState;
  driftSections: string[];
  cpuPercent: number | null;
  memoryUsedBytes: number | null;
  memoryTotalBytes: number | null;
  diskUsedBytes: number;
  diskTotalBytes: number;
}

/** エージェント申告のコンポーネント状態（`node_reported_state.components`）。 */
interface ApiComponent {
  running?: boolean;
  version?: string | null;
  uptimeSec?: number | null;
  lastReloadAt?: string | null;
  message?: string | null;
}

/** `GET /nodes/:id`。一覧の形 + プール・申告状態。 */
export interface ApiNodeDetail extends ApiNode {
  heartbeatTimeoutSec: number;
  /** 詳細だけが返す「提示された指紋」。承認するかどうかの判断材料。 */
  fingerprintMismatchValue: string | null;
  labels: Record<string, string>;
  notes: string | null;
  pool: { id: string; code: string; name: string } | null;
  reported: {
    appliedGeneration: number;
    applyingGeneration: number | null;
    applyState: NodeApplyState;
    applyError: string | null;
    appliedAt: string | null;
    drift: unknown;
    components: Record<string, ApiComponent>;
    reportedAt: string;
  } | null;
}

/* ------------------------------------------------------------- 変換 */

export function toNodeSummary(n: ApiNode): NodeSummary {
  return {
    id: n.id,
    hostname: n.hostname,
    region: n.region,
    // プール既定を継承しているノードは `updateRing` が null。画面は
    // 「未設定」を出せるよう null のまま受け取る。
    ring: n.updateRing,
    role: n.role,
    status: n.status,
    health: n.health,
    displayStatus: n.displayStatus,
    agentVersion: n.agentVersion,
    stackVersion: n.stackVersion,
    // 複数 IP を持つノードは代表 1 本だけ出す（一覧の幅に収めるため）。
    publicIp: n.publicIpv4[0] ?? null,
    lastSeenAt: n.lastHeartbeatAt,
    domainCount: n.allocatedDomains,
    mailboxCount: n.allocatedMailboxes,
    // 台帳値（`server_nodes.storage_bytes_*`）よりハートビートの実測を優先する。
    // 実測が無い（0）ときだけ台帳へ落とす。
    diskUsedBytes: n.diskTotalBytes > 0 ? n.diskUsedBytes : n.storageBytesUsed,
    diskTotalBytes: n.diskTotalBytes > 0 ? n.diskTotalBytes : n.storageBytesTotal,
    queueDepth: n.queueDepth,
    applyState: n.applyState,
    maintenanceMode: n.maintenanceMode,
    outboundEnabled: n.outboundEnabled,
  };
}

export function toNodeDetail(n: ApiNodeDetail): NodeDetail {
  const components = n.reported?.components ?? {};
  return {
    ...toNodeSummary(n),
    ipv6: n.publicIpv6[0] ?? null,
    // OS バージョンはまだエージェントが申告していない（`server_nodes` に列が無い）。
    // 実装されるまで null 固定にし、画面は「—」を出す。
    osVersion: null,
    groupId: n.pool?.id ?? null,
    groupName: n.pool?.name ?? null,
    labels: n.labels,
    capacity: { maxMailboxes: n.maxMailboxes, maxStorageBytes: n.storageBytesTotal },
    acceptingNewMailboxes: n.acceptsNewAllocations,
    desiredGeneration: n.desiredGeneration,
    // 一度も申告が無いノードは「未報告」を null で表す（0 世代と区別する）。
    appliedGeneration: n.reported === null ? null : n.reported.appliedGeneration,
    driftSections: n.driftSections,
    heartbeatTimeoutSec: n.heartbeatTimeoutSec,
    createdAt: n.createdAt,
    allocatedMailboxes: n.allocatedMailboxes,
    fingerprintMismatchAt: n.fingerprintMismatchAt,
    fingerprintMismatchValue: n.fingerprintMismatchValue,
    fingerprintApprovedAt: n.fingerprintApprovedAt,
    nodeFingerprintPrefix: n.nodeFingerprintPrefix,
    services: Object.entries(components).map(([name, c]) => toServiceState(name, c)),
    metrics: {
      cpuPercent: n.cpuPercent,
      memoryUsedBytes: n.memoryUsedBytes,
      memoryTotalBytes: n.memoryTotalBytes,
      diskUsedBytes: n.diskTotalBytes > 0 ? n.diskUsedBytes : n.storageBytesUsed,
      diskTotalBytes: n.diskTotalBytes > 0 ? n.diskTotalBytes : n.storageBytesTotal,
      queueDepth: n.queueDepth,
      // 24 時間の流量はノード単位の時系列（`/nodes/:id/metrics`）が持つ。
      // 概要タイルは 0 を出し、詳細はメトリクスタブで見せる。
      inbound24h: 0,
      outbound24h: 0,
      rejected24h: 0,
      spam24h: 0,
    },
  };
}

/** `components` の 1 エントリを画面のサービス行へ。 */
function toServiceState(name: string, c: ApiComponent): NodeServiceState {
  return {
    name,
    // エージェントは running の真偽しか申告しない。健全性の 3 値へ写す。
    health: c.running === true ? 'healthy' : c.running === false ? 'critical' : 'unknown',
    version: c.version ?? null,
    uptimeSec: c.uptimeSec ?? null,
    lastReloadAt: c.lastReloadAt ?? null,
    message: c.message ?? null,
  };
}
